import React, { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";

// Lightweight currency symbol resolver for standalone util usage.
// Reads the selected currency code cached in localStorage by CurrencyContext
// (falls back to ₺ if not available). This avoids hooks inside a plain helper.
const CURRENCY_SYMBOLS = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const getCachedCurrencySymbol = () => {
  try {
    const code = (typeof localStorage !== "undefined" && localStorage.getItem("pbstudio_currency_code")) || "TRY";
    return CURRENCY_SYMBOLS[code] || "₺";
  } catch (e) {
    return "₺";
  }
};

/**
 * Masked Currency Input for Turkish Lira
 * Format: 000.000,00 (dot for thousands, comma for decimal)
 * 
 * Features:
 * - Auto-formats as user types
 * - No spinner arrows
 * - Only allows numbers
 * - Device locale aware
 * 
 * @param {object} props
 * @param {number|string} props.value - The numeric value
 * @param {function} props.onChange - Callback with parsed number value
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable input
 * @param {boolean} props.showCurrency - Show ₺ prefix (default: true)
 * @param {number} props.maxDecimals - Max decimal places (default: 2)
 */
export function CurrencyInput({ 
  value, 
  onChange, 
  placeholder = "0",
  className = "",
  disabled = false,
  showCurrency = true,
  maxDecimals = 2,
  ...props 
}) {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);
  const cursorPosRef = useRef(0);

  // Format number to Turkish locale display
  const formatForDisplay = (num) => {
    if (num === null || num === undefined || num === '' || isNaN(num)) {
      return '';
    }
    
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '';
    
    // Format with Turkish locale
    return numValue.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals
    });
  };

  // Parse display value to number
  const parseDisplayValue = (display) => {
    if (!display || display === '') return 0;
    
    // Remove all dots (thousands separator) and replace comma with dot (decimal)
    let cleaned = display.replace(/\./g, '').replace(',', '.');
    
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Initialize display value from prop
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (!isNaN(numValue) && numValue !== parseDisplayValue(displayValue)) {
        setDisplayValue(formatForDisplay(numValue));
      }
    }
  }, [value]);

  const handleChange = (e) => {
    const input = e.target;
    const rawValue = input.value;
    
    // Store cursor position before formatting
    const cursorPos = input.selectionStart;
    const prevLength = displayValue.length;
    
    // Remove all non-numeric characters except comma (for decimal)
    let cleaned = rawValue.replace(/[^\d,]/g, '');
    
    // Handle multiple commas - keep only the first one
    const commaIndex = cleaned.indexOf(',');
    if (commaIndex !== -1) {
      const beforeComma = cleaned.substring(0, commaIndex);
      let afterComma = cleaned.substring(commaIndex + 1).replace(/,/g, '');
      
      // Limit decimal places
      afterComma = afterComma.substring(0, maxDecimals);
      
      cleaned = beforeComma + ',' + afterComma;
    }
    
    // Parse to number and format
    const numValue = parseDisplayValue(cleaned);
    
    // Format the display
    let formatted = '';
    if (cleaned === '' || cleaned === '0') {
      formatted = '';
    } else if (cleaned.endsWith(',')) {
      // User is typing decimal - keep the comma
      formatted = formatForDisplay(numValue) + ',';
    } else if (cleaned.includes(',')) {
      // Has decimal part
      const parts = cleaned.split(',');
      const intPart = parseInt(parts[0].replace(/\./g, '')) || 0;
      formatted = intPart.toLocaleString('tr-TR') + ',' + parts[1];
    } else {
      formatted = formatForDisplay(numValue);
    }
    
    setDisplayValue(formatted);
    
    // Call onChange with the numeric value
    if (onChange) {
      onChange(numValue);
    }
    
    // Restore cursor position (accounting for added/removed separators)
    setTimeout(() => {
      if (inputRef.current) {
        const newLength = formatted.length;
        const diff = newLength - prevLength;
        const newCursorPos = Math.max(0, cursorPos + diff);
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    if ([8, 46, 9, 27, 13, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }
    
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return;
    }
    
    // Allow: comma (for decimal)
    if (e.key === ',' || e.key === '.') {
      // Convert dot to comma
      if (e.key === '.') {
        e.preventDefault();
        const input = e.target;
        const pos = input.selectionStart;
        const newValue = displayValue.slice(0, pos) + ',' + displayValue.slice(pos);
        setDisplayValue(newValue);
        setTimeout(() => {
          input.setSelectionRange(pos + 1, pos + 1);
        }, 0);
      }
      return;
    }
    
    // Block non-numeric keys
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    // Clean up display on blur (remove trailing comma, format properly)
    if (displayValue) {
      const numValue = parseDisplayValue(displayValue);
      setDisplayValue(formatForDisplay(numValue));
    }
  };

  return (
    <div className="relative">
      {showCurrency && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-500 pointer-events-none">
          ₺
        </span>
      )}
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`${showCurrency ? 'pl-7' : ''} ${className}`}
        style={{ 
          // Hide number spinners if browser renders them
          MozAppearance: 'textfield',
          WebkitAppearance: 'none'
        }}
        {...props}
      />
    </div>
  );
}

/**
 * Simple currency display formatter
 */
export function formatCurrency(value, showSymbol = true) {
  const symbol = getCachedCurrencySymbol();
  if (value === null || value === undefined || value === '') return showSymbol ? `${symbol}0` : '0';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return showSymbol ? `${symbol}0` : '0';
  
  const formatted = num.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  
  return showSymbol ? `${symbol}${formatted}` : formatted;
}

export default CurrencyInput;
